// Wallet Home Page
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { 
  Plus, ArrowUpRight, ArrowLeftRight, RefreshCw, Eye, EyeOff, 
  Bell, LogOut, Smartphone, Shield, ChevronRight, Sparkles, TrendingUp
} from "lucide-react";
import { SBER, API, pageVariants, tapAnimation, openExternalLink } from "../utils/constants";
import { SberButton, SberCard, TransactionItem } from "../components/UIComponents";

const WalletHome = ({ user, rate, transactions, onNavigate, onRefresh, notificationCount = 0, onLogout, onUpdateUser, t }) => {
  const [showBalance, setShowBalance] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [mdlUsdRate, setMdlUsdRate] = useState(null);
  
  // Fetch MDL/USD rate
  useEffect(() => {
    const fetchMdlRate = async () => {
      try {
        const res = await axios.get(`${API}/exchange/mdl-usd`);
        if (res.data.success) {
          setMdlUsdRate(res.data);
        }
      } catch (err) {
        console.error('Error fetching MDL/USD rate:', err);
      }
    };
    
    fetchMdlRate();
    // Refresh every 5 minutes
    const interval = setInterval(fetchMdlRate, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);
  
  // All currency balances
  const balances = {
    USD: { value: user?.balance_usd || 0, name: t('currencies.USD'), flag: '🇺🇸', color: '#059669' },
    KGS: { value: user?.balance_kgs || 0, name: t('currencies.KGS'), flag: '🇰🇬', color: '#DC2626' },
    EUR: { value: user?.balance_eur || 0, name: t('currencies.EUR'), flag: '🇪🇺', color: '#7C3AED' },
    MDL: { value: user?.balance_mdl || 0, name: t('currencies.MDL'), flag: '🇲🇩', color: '#0369A1' },
    KZT: { value: user?.balance_kzt || 0, name: t('currencies.KZT'), flag: '🇰🇿', color: '#0891B2' },
    RUB: { value: user?.balance_rub || 0, name: t('currencies.RUB'), flag: '🇷🇺', color: '#B91C1C' },
  };
  
  const handleRefresh = async () => {
    setRefreshing(true);
    await onRefresh();
    setRefreshing(false);
  };
  
  // Currency card component
  const CurrencyCard = ({ code, data, onClick, large }) => (
    <SberCard className={large ? "p-4" : "p-3"} onClick={onClick} data-testid={`balance-card-${code}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div 
            className={`${large ? 'w-12 h-12' : 'w-10 h-10'} rounded-xl flex items-center justify-center ${large ? 'text-xl' : 'text-lg'}`}
            style={{ background: data.isCrypto ? data.color : `${data.color}15` }}
          >
            {data.isCrypto ? (
              <span className="text-white font-bold">{data.flag}</span>
            ) : (
              data.flag
            )}
          </div>
          <div>
            <p className="text-[12px]" style={{ color: SBER.gray }}>{data.name}</p>
            <p className={`${large ? 'text-[20px]' : 'text-[17px]'} font-bold`} style={{ color: SBER.dark }}>
              {showBalance ? data.value.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '••••'}
              <span className="text-[12px] font-medium ml-1" style={{ color: SBER.gray }}>{code}</span>
            </p>
          </div>
        </div>
        <ChevronRight className="w-4 h-4" style={{ color: SBER.grayLight }} />
      </div>
    </SberCard>
  );
  
  return (
    <motion.div 
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="min-h-screen pb-28"
      style={{ background: SBER.bg }}
    >
      {/* Header */}
      <div className="px-5 pt-5 pb-4 flex items-center justify-between" style={{ background: SBER.white }}>
        <div className="flex items-center gap-3">
          <img 
            src="/polytech-logo.png" 
            alt="PolyTech Wallet" 
            className="w-11 h-11 rounded-xl object-contain"
            style={{ background: 'linear-gradient(135deg, #1a472a 0%, #22c55e 100%)' }}
          />
          <div>
            <p className="font-bold text-[17px]" style={{ color: SBER.dark }}>
              PolyTech Wallet
            </p>
            <p className="text-[12px]" style={{ color: SBER.green }}>
              {user?.first_name ? `${t('home.greeting')}, ${user.first_name}!` : t('home.multiCurrencyWallet')}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Notifications Button */}
          <motion.button 
            whileTap={tapAnimation}
            onClick={() => onNavigate('notifications')}
            className="w-10 h-10 rounded-full flex items-center justify-center relative"
            style={{ background: SBER.grayPale }}
            data-testid="notifications-btn"
          >
            <Bell className="w-[18px] h-[18px]" style={{ color: SBER.gray }} />
            {notificationCount > 0 && (
              <span 
                className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full flex items-center justify-center text-[10px] font-bold text-white"
                style={{ background: '#EF4444' }}
              >
                {notificationCount > 9 ? '9+' : notificationCount}
              </span>
            )}
          </motion.button>
          <motion.button 
            whileTap={tapAnimation}
            onClick={handleRefresh}
            className="w-10 h-10 rounded-full flex items-center justify-center"
            style={{ background: SBER.grayPale }}
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-[18px] h-[18px] ${refreshing ? 'animate-spin' : ''}`} style={{ color: SBER.gray }} />
          </motion.button>
          <motion.button 
            whileTap={tapAnimation}
            onClick={() => setShowBalance(!showBalance)}
            className="w-10 h-10 rounded-full flex items-center justify-center"
            style={{ background: SBER.grayPale }}
          >
            {showBalance ? <Eye className="w-[18px] h-[18px]" style={{ color: SBER.gray }} /> : <EyeOff className="w-[18px] h-[18px]" style={{ color: SBER.gray }} />}
          </motion.button>
          {onLogout && (
            <motion.button 
              whileTap={tapAnimation}
              onClick={onLogout}
              className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{ background: '#FEE2E2' }}
              data-testid="logout-btn"
            >
              <LogOut className="w-[18px] h-[18px] text-red-500" />
            </motion.button>
          )}
        </div>
      </div>
      
      {/* MDL/USD Exchange Rate Banner */}
      <div className="px-5 pt-4 pb-2">
        <SberCard className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[12px] font-medium mb-1" style={{ color: SBER.gray }}>
                {t('home.exchangeRates') || 'Курс валют'}
              </p>
              <div className="flex items-baseline gap-2">
                <span className="text-[24px] font-bold" style={{ color: SBER.green }}>
                  {mdlUsdRate?.rate ? (mdlUsdRate.rate * 1.005).toFixed(4) : '—'}
                </span>
                <span className="text-[14px]" style={{ color: SBER.gray }}>MDL/USD</span>
              </div>
              <p className="text-[11px] mt-1" style={{ color: SBER.grayLight }}>
                {mdlUsdRate?.source || 'BNM'} • {t('home.updatesEvery5min') || 'обновление 5 мин'}
              </p>
            </div>
            <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ background: SBER.greenPale }}>
              <TrendingUp className="w-7 h-7" style={{ color: SBER.green }} />
            </div>
          </div>
        </SberCard>
      </div>
      
      {/* USD Balance - Featured */}
      <div className="px-5 pt-4 pb-2">
        <CurrencyCard code="USD" data={balances.USD} onClick={() => onNavigate('exchange')} large />
      </div>
      
      {/* Other Currencies Section */}
      <div className="px-5 py-3">
        <p className="text-[13px] font-semibold mb-3" style={{ color: SBER.gray }}>{t('home.currencies')}</p>
        <div className="grid grid-cols-2 gap-2">
          <CurrencyCard code="KGS" data={balances.KGS} onClick={() => onNavigate('currency', { currency: 'KGS' })} />
          <CurrencyCard code="EUR" data={balances.EUR} onClick={() => onNavigate('currency', { currency: 'EUR' })} />
          <CurrencyCard code="MDL" data={balances.MDL} onClick={() => onNavigate('mia')} />
          <CurrencyCard code="KZT" data={balances.KZT} onClick={() => onNavigate('currency', { currency: 'KZT' })} />
          <CurrencyCard code="RUB" data={balances.RUB} onClick={() => onNavigate('topup')} />
        </div>
      </div>
      
      {/* Quick Actions */}
      <div className="px-5 mb-4">
        <div className="flex justify-between">
          <SberButton icon={Plus} label={t('actions.topUp')} primary onClick={() => onNavigate('topup')} />
          <SberButton icon={ArrowLeftRight} label={t('actions.exchange')} onClick={() => onNavigate('exchange')} />
          <SberButton icon={ArrowUpRight} label={t('actions.withdraw')} onClick={() => onNavigate('withdraw')} />
          <SberButton icon={Smartphone} label={t('actions.payments')} onClick={() => onNavigate('payments')} />
        </div>
      </div>
      
      {/* KYC Banner */}
      {user?.kyc_status !== 'APPROVED' && (
        <div className="px-5 mb-4">
          <SberCard 
            className="p-4 cursor-pointer" 
            onClick={async () => {
              try {
                const loadingToast = toast.loading(t('kyc.creatingSession'));
                const response = await fetch(`${API}/kyc/session`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('wallet_token') || ''}`,
                    'X-Telegram-Id': user?.telegram_id?.toString() || ''
                  },
                  body: JSON.stringify({})
                });
                const data = await response.json();
                toast.dismiss(loadingToast);
                
                if (data.success && data.verification_url) {
                  openExternalLink(data.verification_url);
                  toast.success(t('kyc.openNewTab'));
                  if (onUpdateUser) onUpdateUser(prev => ({ ...prev, kyc_status: 'IN_PROGRESS' }));
                } else if (data.kyc_status === 'APPROVED') {
                  toast.success(t('kyc.alreadyPassed'));
                  if (onUpdateUser) onUpdateUser(prev => ({ ...prev, kyc_status: 'APPROVED' }));
                } else {
                  toast.error(data.detail || t('kyc.sessionError'));
                }
              } catch (error) {
                toast.error(t('errors.connectionError'));
              }
            }}
            data-testid="kyc-banner"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: SBER.greenPale }}>
                <Shield className="w-5 h-5" style={{ color: SBER.green }} strokeWidth={2} />
              </div>
              <div className="flex-1">
                <p className="font-medium text-[15px]" style={{ color: SBER.dark }}>{t('kyc.verification')}</p>
                <p className="text-[13px]" style={{ color: SBER.grayLight }}>
                  {user?.kyc_status === 'IN_PROGRESS' || user?.kyc_status === 'PENDING' || user?.kyc_status === 'IN_REVIEW'
                    ? t('kyc.continueCheck') 
                    : t('kyc.forWithdrawal')}
                </p>
              </div>
              <ArrowUpRight className="w-5 h-5" style={{ color: SBER.grayLight }} />
            </div>
          </SberCard>
        </div>
      )}
      
      {/* Recent Transactions */}
      <div className="px-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-[16px]" style={{ color: SBER.dark }}>
            {t('home.recentTransactions')}
          </h3>
          <motion.button 
            whileTap={tapAnimation}
            onClick={() => onNavigate('history')}
            className="text-[13px] font-medium"
            style={{ color: SBER.green }}
          >
            {t('home.allTransactions')} →
          </motion.button>
        </div>
        
        {transactions.length === 0 ? (
          <SberCard className="p-8 text-center">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-3" style={{ background: SBER.greenPale }}>
              <Sparkles className="w-6 h-6" style={{ color: SBER.green }} />
            </div>
            <p className="font-medium" style={{ color: SBER.gray }}>{t('home.noTransactions')}</p>
            <p className="text-[13px] mt-1" style={{ color: SBER.grayLight }}>{t('home.topUpBalance')}</p>
          </SberCard>
        ) : (
          <div className="space-y-2">
            {transactions.slice(0, 3).map((tx, idx) => (
              <TransactionItem key={tx.id || idx} tx={tx} t={t} />
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default WalletHome;
